import {
  type ScoredHint,
  type RoomModifier,
  type RoomConfig,
  type Room,
  type GhostHint,
  type LootItem,
  DegenerateType,
  PowerUpTier,
} from "./types";
import { evaluateGuess } from "./evaluator";
import { generateCode } from "./generator";
import { generateHintPool } from "./hintPool";
import { assemblePuzzle } from "./assembler";
import { findOptimalPath } from "./optimalPath";

const ROOM_MODIFIERS: readonly RoomModifier[] = [
  { id: "clean", name: "STANDARD", description: "No anomalies detected.", minRoom: 1 },
  { id: "sparse", name: "SIGNAL LOSS", description: "Fewer intercepted signals.", minRoom: 3 },
  { id: "noisy", name: "GHOST DATA", description: "One signal may be unreliable.", minRoom: 5 },
  { id: "locked", name: "LOCKED TERMINAL", description: "No signal decoding available.", minRoom: 4 },
  { id: "dark", name: "POWER FLUCTUATION", description: "One signal hidden until first attempt.", minRoom: 6 },
];

function selectModifier(roomNumber: number): RoomModifier {
  if (roomNumber < 3) {
    return ROOM_MODIFIERS[0];
  }

  const eligible = ROOM_MODIFIERS.filter((m) => m.minRoom <= roomNumber);
  const weights = eligible.map((m) => (roomNumber >= m.minRoom + 3 ? 2 : 1));
  const totalWeight = weights.reduce((a, b) => a + b, 0);

  let r = Math.random() * totalWeight;
  for (let i = 0; i < eligible.length; i++) {
    r -= weights[i];
    if (r <= 0) {
      return eligible[i];
    }
  }

  return eligible[eligible.length - 1];
}

function getReserveCount(modifier: RoomModifier): number {
  if (modifier.id === "locked") return 0;
  if (modifier.id === "sparse") return 2;
  if (modifier.id === "noisy") return 3;
  if (modifier.id === "dark") return 3;
  return 5;
}

export function getRoomConfig(roomNumber: number): RoomConfig {
  let codeLength = 3;
  if (roomNumber >= 10) {
    codeLength = 4;
  } else if (roomNumber >= 8) {
    codeLength = Math.random() > 0.5 ? 4 : 3;
  }

  let baseHintTarget = 5;
  if (roomNumber >= 12) {
    baseHintTarget = 3;
  } else if (roomNumber >= 7) {
    baseHintTarget = 4;
  }

  const modifier = selectModifier(roomNumber);
  const reserveCount = getReserveCount(modifier);

  return { codeLength, baseHintTarget, modifier, reserveCount, roomNumber };
}

function createGhostHint(
  solution: readonly number[],
  codeLength: number,
): GhostHint {
  const fakeGuess: number[] = [];
  for (let i = 0; i < codeLength; i++) {
    fakeGuess.push(Math.floor(Math.random() * 10));
  }

  const real = evaluateGuess(solution, fakeGuess);

  const fakePlaced = Math.max(
    0,
    real.correct_placed + (Math.random() > 0.5 ? 1 : -1),
  );
  const clampedPlaced = Math.min(fakePlaced, codeLength);
  const fakeMisplaced = Math.max(
    0,
    real.correct_misplaced + (Math.random() > 0.5 ? 1 : -1),
  );
  const clampedMisplaced = Math.min(fakeMisplaced, codeLength - clampedPlaced);

  const rarityOptions = ["uncommon", "rare", "epic"] as const;
  const rarity = rarityOptions[Math.floor(Math.random() * rarityOptions.length)];

  return {
    digits: fakeGuess,
    correct_placed: clampedPlaced,
    correct_misplaced: clampedMisplaced,
    rarity,
    isGhost: true,
  };
}

function constructOracleFromSolution(
  solution: readonly number[],
): ScoredHint {
  const perm = [...solution];
  for (let i = perm.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * i);
    [perm[i], perm[j]] = [perm[j], perm[i]];
  }

  if (perm.every((d, idx) => d === solution[idx])) {
    [perm[0], perm[1]] = [perm[1], perm[0]];
  }

  const { correct_placed, correct_misplaced } = evaluateGuess(solution, perm);

  return {
    hint: { digits: perm, correct_placed, correct_misplaced },
    rarity: null,
    elimination_rate: 0,
    percentile: 0,
    degenerate_type: DegenerateType.ANAGRAM,
    power_up: PowerUpTier.ORACLE,
    candidates_before: 0,
    candidates_after: 0,
    distinct_digits_tested: new Set(perm).size,
    unique_correct_digits: new Set(solution).size,
  };
}

export function generateRoom(
  roomNumber: number,
  config?: RoomConfig,
): Room {
  const roomConfig = config ?? getRoomConfig(roomNumber);
  const { codeLength, baseHintTarget, modifier, reserveCount } = roomConfig;

  const solution = generateCode(codeLength);
  if (solution === null) {
    throw new Error(`Failed to generate code of length ${codeLength}`);
  }

  const digitRange: [number, number] = [0, 9];
  const fullPool = generateHintPool(solution, digitRange);

  const cleanPool = fullPool.filter(
    (h) => h.degenerate_type === DegenerateType.NONE,
  );
  const degeneratePool = fullPool.filter(
    (h) => h.degenerate_type !== DegenerateType.NONE,
  );

  const { hints: baseHints, isUnique } = assemblePuzzle(
    cleanPool,
    codeLength,
    baseHintTarget,
  );

  let ghostHint: GhostHint | null = null;
  if (modifier.id === "noisy") {
    ghostHint = createGhostHint(solution, codeLength);
  }

  let hiddenHintIndex: number | null = null;
  if (modifier.id === "dark" && baseHints.length > 1) {
    hiddenHintIndex = Math.floor(Math.random() * baseHints.length);
  }

  const realHints = baseHints;
  const optimalIndices = findOptimalPath(solution, realHints.map((h) => h.hint), digitRange);
  const optimalSize = optimalIndices.length;

  const usedKeys = new Set(baseHints.map((h) => h.hint.digits.join(",")));
  const reserve = cleanPool
    .filter((h) => !usedKeys.has(h.hint.digits.join(",")))
    .sort((a, b) => b.elimination_rate - a.elimination_rate)
    .slice(0, reserveCount);

  const roomPowerUps: ScoredHint[] = [];

  const oracleHints = degeneratePool.filter(
    (h) => h.power_up === PowerUpTier.ORACLE,
  );
  if (oracleHints.length > 0) {
    roomPowerUps.push(oracleHints[0]);
  } else {
    roomPowerUps.push(constructOracleFromSolution(solution));
  }

  const surgeHints = degeneratePool.filter(
    (h) =>
      h.power_up === PowerUpTier.SURGE_I || h.power_up === PowerUpTier.SURGE_II,
  );
  if (surgeHints.length > 0) {
    roomPowerUps.push(surgeHints[0]);
  }

  const boostHints = degeneratePool.filter(
    (h) => h.power_up === PowerUpTier.BOOST,
  );
  if (boostHints.length > 0) {
    roomPowerUps.push(boostHints[0]);
  }

  let loot: LootItem | null = null;
  if (Math.random() < 0.4) {
    const lootOptions: LootItem[] = [];
    if (oracleHints.length > 0) {
      lootOptions.push({
        digits: oracleHints[0].hint.digits,
        correct_placed: oracleHints[0].hint.correct_placed,
        correct_misplaced: oracleHints[0].hint.correct_misplaced,
        lootType: "oracle",
        power_up: PowerUpTier.ORACLE,
        degenerate_type: DegenerateType.ANAGRAM,
      });
    }
    const surgePool = degeneratePool.filter(
      (h) =>
        h.power_up === PowerUpTier.SURGE_I ||
        h.power_up === PowerUpTier.SURGE_II,
    );
    if (surgePool.length > 0) {
      lootOptions.push({
        digits: surgePool[0].hint.digits,
        correct_placed: surgePool[0].hint.correct_placed,
        correct_misplaced: surgePool[0].hint.correct_misplaced,
        lootType: "surge",
        power_up: surgePool[0].power_up!,
        degenerate_type: surgePool[0].degenerate_type,
      });
    }
    if (boostHints.length > 0) {
      lootOptions.push({
        digits: boostHints[0].hint.digits,
        correct_placed: boostHints[0].hint.correct_placed,
        correct_misplaced: boostHints[0].hint.correct_misplaced,
        lootType: "boost",
        power_up: PowerUpTier.BOOST,
        degenerate_type: DegenerateType.REPEATED_DIGITS,
      });
    }
    if (lootOptions.length > 0) {
      loot = lootOptions[Math.floor(Math.random() * lootOptions.length)];
    }
  }

  const bonusLives = Math.random() < 0.25 ? 1 : 0;

  return {
    solution,
    baseHints,
    reserveHints: reserve,
    roomPowerUps,
    isUnique,
    optimalSize,
    optimalIndices,
    modifier,
    roomNumber,
    codeLength,
    hiddenHintIndex,
    loot,
    bonusLives,
    ghostHint,
  };
}
